import classNames from 'classnames';
import { PrimaryButton } from '@/common/Button';
import { useGetShowEVTaxTreatment } from '~/hooks/vdp';
import { useButtonHandler } from '~/hooks/vdp/cta';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import {
  CardContents,
  LineItem,
  Price,
  PriceContainer,
  PriceBreakdown,
  SummaryLineItem
} from './Card.styles';
import usePaymentSummary from '../../hooks/selectors/useGetPaymentSummary';
import useGetStartedText from '../../hooks/selectors/useGetStartedText';
import CkPromoLineItem from '../CkPromoLineItem';
import { CleanTaxSavingsCard } from '../CleanTaxSavingsCard';
import TradeInSection from '../TradeInSection';

export default function PayOnceCard() {
  const {
    credits: { creditKarmaPromoAmount, tradeInCredit, totalDiscountAmount },
    debits: { deliveryCharge, taxTitleReg, vehiclePrice },
    totals: { totalPrice, subTotal }
  } = usePaymentSummary();
  const getStartedText = useGetStartedText();
  const showSubtotal = Boolean(tradeInCredit);
  const getStarted = useButtonHandler();
  const shouldShowEvTreatment = useGetShowEVTaxTreatment();
  const discountLineItem = creditKarmaPromoAmount
    ? totalDiscountAmount - creditKarmaPromoAmount
    : totalDiscountAmount;

  return (
    <section data-qa="pay-once-card">
      <CardContents>
        <PriceContainer className="mb-24">
          <Price>${formatAndDropDecimal(totalPrice)}</Price>
          {!deliveryCharge && <p className="tl-body-l">Free Shipping</p>}
          {shouldShowEvTreatment && (
            <CleanTaxSavingsCard
              className={classNames(
                'max-w-[448px]',
                deliveryCharge ? 'mt-16' : 'mt-8'
              )}
            />
          )}
        </PriceContainer>

        <PriceBreakdown>
          <ul className="mb-12 m:mb-24">
            <LineItem>
              <span>Vehicle price</span>
              <span>${formatAndDropDecimal(vehiclePrice)}</span>
            </LineItem>
            <LineItem className={creditKarmaPromoAmount > 0 ? '' : 'hidden'}>
              <CkPromoLineItem />
              <span>- ${formatAndDropDecimal(creditKarmaPromoAmount)}</span>
            </LineItem>
            <LineItem className={discountLineItem > 0 ? '' : 'hidden'}>
              <span>Discount</span>
              <span>- ${formatAndDropDecimal(discountLineItem)}</span>
            </LineItem>
            <LineItem data-qa="shipping-fee-line">
              <span>{deliveryCharge ? 'Nonrefundable s' : 'S'}hipping fee</span>
              <span>${formatAndDropDecimal(deliveryCharge)}</span>
            </LineItem>
            <LineItem className="mb-0">
              <span>Tax, title, registration</span>
              <span>${formatAndDropDecimal(taxTitleReg)}</span>
            </LineItem>
          </ul>

          <ul
            className={classNames('mb-24 border-t border-grey-2 pt-12', {
              hidden: !showSubtotal
            })}
            data-qa="trade-in-details"
          >
            <SummaryLineItem>
              <span>Subtotal</span>
              <span>${formatAndDropDecimal(subTotal)}</span>
            </SummaryLineItem>
            <TradeInSection
              tradeInCredit={tradeInCredit}
              hasDeliveryCharge={Boolean(deliveryCharge)}
              card="once"
            />
          </ul>

          <ul>
            <div
              className={`rounded-[8px] border border-solid border-grey-3 p-16 ${
                showSubtotal ? 'mt-24' : ''
              }`}
            >
              <SummaryLineItem className="mb-0">
                <span>Total Purchase Price</span>
                <span>${formatAndDropDecimal(totalPrice)}</span>
              </SummaryLineItem>
              <li
                className={classNames(
                  'mt-8 text-grey-5 tl-body-xs m:tl-body-s',
                  {
                    hidden: !deliveryCharge
                  }
                )}
              >
                Includes ${formatAndDropDecimal(deliveryCharge)} nonrefundable
                shipping fee
              </li>
            </div>
          </ul>
        </PriceBreakdown>

        <div
          className={
            getStartedText
              ? 'mx-auto mt-24 w-full max-w-[448px] m:max-w-[250px]'
              : 'hidden'
          }
        >
          <PrimaryButton
            className="!w-full"
            data-analytics-id="Price Details - Pay Once - Get Started"
            onClick={getStarted}
            size="medium"
          >
            {getStartedText}
          </PrimaryButton>
        </div>
      </CardContents>
    </section>
  );
}
